import {useRef} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";
import {API} from "../api";
import {useImages} from "contexts/images";
import {useSetting} from "contexts/setting";
import {getChannelSettingsQueryKey} from "hooks/useChannelSettings";
import {removeSessionBgUpload, sessionBgScope, type SessionBgUpload} from "libs/sessionBgUploads";
import {writeUrlBackground} from "libs/bgNumbering";

export interface PromoteUploadInput {
  /** The upload to make a background. Send this or `variation`. */
  uploadId?: string;
  /**
   * A background of the gallery that an admin hid on the page: a saved link
   * shows it with the link tile. It becomes the first one, offered again.
   */
  variation?: number;
  /** The temporary tile of the image (libs/sessionBgUploads.ts). It goes. */
  sessionUpload?: SessionBgUpload | null;
  /**
   * The background option the page shows the image as: the temporary tile's
   * variation or "custom". If the page still shows it when the image is a
   * background, the page shows the new background instead.
   */
  shownAs?: string | null;
  /** The releases set that alone gets the background. Undefined: every set. */
  set?: string;
}

export interface PromoteUploadResult {
  variation: string;
  /** The page shows the new background now. */
  isShown: boolean;
}

/**
 * Make an uploaded image a background of the product for everyone: the first
 * one of its published gallery (POST /api/v3/bg-list/first). Resolves when the
 * gallery lists it.
 *
 * "Save as default" (DefaultScenePanel), the "+" of a temporary upload tile
 * and the "+" of a saved link's tile (BgSelector) all use it. The link tile
 * can also hold a background that an admin hid on the page: then the gallery
 * puts that background first and offers it again.
 */
export function usePromoteUpload() {
  const {dataChannel, dataType, product, bgOption, editBgOption}: any = useSetting();
  const {userBgAdjustments, setUserBgAdjustment}: any = useImages();
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  // The page can change while the server works: read it when the answer comes.
  const latest = useRef({bgOption, userBgAdjustments});
  latest.current = {bgOption, userBgAdjustments};
  return useMutation({
    mutationFn: async ({
      uploadId,
      variation: listed,
      sessionUpload,
      shownAs,
      set,
    }: PromoteUploadInput): Promise<PromoteUploadResult> => {
      const {variation} = await API.promoteBgUploadFirst({
        channel: dataChannel,
        type: dataType,
        product: product.value,
        uploadId,
        variation: listed,
        set,
      });
      const promoted = String(variation);
      // The gallery must list the new background before the page selects it.
      // The channel settings too: the server offers it again where an admin
      // hid it (offerBackgroundFirst, channel-settings.ts).
      await queryClient.invalidateQueries({queryKey: ["v3-backgrounds"]});
      await queryClient.invalidateQueries({queryKey: ["bg-list"]});
      await queryClient.invalidateQueries({queryKey: getChannelSettingsQueryKey(dataChannel)});
      const isShown = shownAs != null && String(latest.current.bgOption) === shownAs;
      if (isShown) {
        // The crop set on the image stays with it.
        const adjustments = latest.current.userBgAdjustments?.[shownAs] ?? {};
        for (const [id, adjustment] of Object.entries(adjustments)) {
          setUserBgAdjustment(promoted, id, adjustment);
        }
        editBgOption(promoted);
        // By identity: the new background leads the gallery, and the page
        // gives it its number when the numbers come (useBackgroundNumbers).
        setSearchParams((params) => writeUrlBackground(params, promoted, null), {replace: true});
      }
      if (sessionUpload) {
        removeSessionBgUpload(
          sessionBgScope(dataChannel, dataType, product.value),
          sessionUpload.entryKey
        );
      }
      return {variation: promoted, isShown};
    },
  });
}
